import React, { useState } from "react";
import { Info, Phone } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import defaultImg from "../Images/liquiddrain.avif";
import img from "../Images/ballfloat.jpeg";
import img1 from "../Images/thermosteam.avif";
import img2 from "../Images/invstm.webp";
import img3 from "../Images/liqspec.png";
import img4 from "../Images/ballflspec.png";
import img5 from "../Images/thermo.png";
import img6 from "../Images/invtbuckspec.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState("description");

  return (
    <article 
      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6 lg:p-8"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-gray-900" itemProp="name">
          {product.name}
        </h3>
      </header>
      
      <div className="bg-gray-100 h-48 sm:h-56 md:h-64 lg:h-72 mb-4 rounded-lg overflow-hidden">
        <img
          src={product.mainImage}
          alt={`${product.name} - High-efficiency condensate removal device by Gortherm Energy`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          itemProp="image"
          loading="lazy"
          width="600"
          height="400"
        />
      </div>

      <nav className="mb-4">
        <div className="flex border-b border-gray-200" role="tablist" aria-label="Product information">
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === "description"
                ? "border-b-2 border-teal-600 text-teal-600 font-semibold"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("description")}
            aria-selected={activeTab === "description"}
            role="tab"
            aria-controls="description-panel"
          >
            Description
          </button>
          <button
            className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 text-xs sm:text-sm lg:text-base transition-colors ${
              activeTab === "specs"
                ? "border-b-2 border-teal-600 text-teal-600 font-semibold"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
            onClick={() => setActiveTab("specs")}
            aria-selected={activeTab === "specs"}
            role="tab"
            aria-controls="specs-panel"
          >
            <span className="hidden sm:inline">Technical Specifications</span>
            <span className="sm:hidden">Specs</span>
          </button>
        </div>
      </nav>

      <div className="mt-4" role="tabpanel" id={activeTab === "description" ? "description-panel" : "specs-panel"}>
        {activeTab === "description" ? (
          <p className="text-gray-700 text-justify text-sm sm:text-base leading-relaxed" itemProp="description">
            {product.description}
          </p>
        ) : (
          <div>
            {product.specImage && (
              <div className="bg-gray-100 h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden mb-4">
                <img
                  src={product.specImage}
                  alt={`${product.name} detailed technical specifications and measurements`}
                  className="w-full h-full object-contain p-2"
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            )}
            <ul className="space-y-2 sm:space-y-3">
              {product.specifications.map((spec, index) => (
                <li key={index} className="flex items-start">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700 text-sm sm:text-base">{spec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
};

const SteamTraps = () => {
  const products = [
    {
      id: "liquid-drain",
      name: "Liquid Drain Traps",
      mainImage: defaultImg,
      specImage: img3,
      description: `Gortherm Energy's Liquid Drain Traps are engineered for efficient condensate removal in steam systems. These traps excel in applications where continuous drainage is essential for maintaining system efficiency. The innovative design ensures reliable operation even under varying pressure conditions, while the robust construction guarantees long-term performance. Our liquid drain traps feature advanced sealing technology that prevents steam loss while efficiently removing condensate, making them ideal for process applications where maintaining steam quality is crucial.`,
      specifications: [
        "Operating Pressure Range: 0.5 to 80 bar",
        "Temperature Range: Up to 450°C",
        "Body Material: Carbon Steel, Stainless Steel options",
        "Connection Types: Flanged, Socket Weld, or Threaded",
        "Integral strainer for debris protection",
        "Continuous discharge capability",
        "Available sizes: DN15 to DN50",
      ],
    },
    {
      id: "ball-float",
      name: "Ball Float Steam Trap",
      mainImage: img,
      specImage: img4,
      description: `Our Ball Float Steam Traps represent the pinnacle of modulating discharge technology. These traps provide continuous condensate drainage while maintaining steam efficiency through their precision-engineered floating ball mechanism. The design allows for immediate condensate discharge, preventing water logging in steam systems. Gortherm Energy's ball float traps feature an auxiliary thermostatic air vent for efficient air removal during system startup, ensuring optimal heating efficiency and reduced energy consumption.`,
      specifications: [
        "Maximum Operating Pressure: 32 bar",
        "Maximum Temperature: 400°C",
        "Construction: SG Iron or Cast Steel body",
        "Integral air venting mechanism",
        "Replaceable valve seat and ball",
        "Built-in Y-type strainer",
        "Available in horizontal and vertical configurations",
      ],
    },
    {
      id: "thermodynamic",
      name: "Thermodynamic Steam Traps",
      mainImage: img1,
      specImage: img5,
      description: `Gortherm Energy's Thermodynamic Steam Traps are designed for rugged reliability in challenging environments. These traps operate on the disc principle, providing intermittent discharge of condensate close to steam temperature. Their simple yet effective design makes them ideal for superheated steam applications and outdoor installations. The unique disc operation ensures tight shut-off against steam while efficiently discharging condensate, making these traps particularly suitable for steam main drainage and high-pressure applications.`,
      specifications: [
        "Working Pressure: Up to 120 bar",
        "Temperature Range: Up to 500°C",
        "All stainless steel construction",
        "Integral insulating cover",
        "Renewable valve seat and disc",
        "Compact and lightweight design",
        "Universal mounting position capability",
      ],
    },
    {
      id: "inverted-bucket",
      name: "Inverted Bucket Steam Traps",
      mainImage: img2,
      specImage: img6,
      description: `The Inverted Bucket Steam Traps from Gortherm Energy combine time-tested principles with modern engineering excellence. These traps offer exceptional resistance to water hammer and dirt, making them ideal for severe service conditions. The unique leverage system multiplies the force from the bucket to provide a strong seal against steam loss. Our inverted bucket traps feature a deep water seal that prevents steam loss and ensures energy-efficient operation, while their robust construction handles thermal cycling and water hammer with ease.`,
      specifications: [
        "Operating Pressure: Up to 45 bar",
        "Maximum Temperature: 450°C",
        "Materials: Cast Iron, Cast Steel, or Stainless Steel",
        "Large condensate handling capacity",
        "Self-adjusting variable discharge slot",
        "Integral strainer option available",
        "Bi-metallic air vent for rapid air removal",
      ],
    },
  ];

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Steam Traps & Condensate Removal Systems",
    "description": "High-efficiency steam traps including liquid drain, ball float, thermodynamic, and inverted bucket designs for optimal condensate removal",
    "itemListElement": products.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.name,
      "description": product.description,
      "image": product.mainImage,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Gortherm Energy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Steam Traps & Condensate Removal Systems | Ball Float, Thermodynamic | Gortherm Energy</title>
        <meta name="description" content="High-efficiency steam traps including liquid drain traps, ball float steam traps, thermodynamic steam traps, and inverted bucket traps. Precision-engineered condensate removal systems for optimal steam system performance." />
        <meta name="keywords" content="steam traps, condensate removal, ball float steam trap, thermodynamic steam trap, inverted bucket trap, liquid drain trap, steam system efficiency, condensate drainage, industrial steam traps, energy efficient steam traps Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Steam Traps & Condensate Removal Systems | Gortherm Energy" />
        <meta property="og:description" content="Advanced steam traps for efficient condensate removal and optimal steam system performance. Complete range of trap types for all applications." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/steam-traps" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Steam Traps & Condensate Removal Systems | Gortherm Energy" />
        <meta name="twitter:description" content="Precision-engineered steam traps for reliable condensate removal and energy-efficient steam system operation." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/steam-traps" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <header className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Steam Traps & Condensate Removal Systems
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Explore our advanced range of Steam Traps, engineered with precision
              for optimal performance and reliable control in demanding industrial
              applications.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <aside className="mt-12 sm:mt-16 lg:mt-20 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Need Expert Guidance?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 px-4">
                Our specialists are here to help you select the perfect steam trap
                solution for your application and ensure optimal condensate removal.
              </p>
              <a 
                href="tel:+9779802781400"
                className="inline-block"
                aria-label="Contact steam trap experts at +977 980 278 1400"
              >
                <button className="bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span>Contact Our Experts</span>
                </button>
              </a>
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                Call us at: <a href="tel:+9779802781400" className="text-teal-600 hover:text-teal-700 font-semibold">+977 980 278 1400</a>
              </p>
            </div>
          </aside>

          {/* Additional SEO Content Section */}
          <section className="mt-12 sm:mt-16 bg-white rounded-xl p-6 sm:p-8 shadow-md">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
              Why Choose Gortherm Energy Steam Traps?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Energy Efficiency</h3>
                <p className="text-sm sm:text-base">
                  Our steam traps maximize energy savings by efficiently removing condensate while preventing 
                  steam loss, reducing fuel consumption and operational costs.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Reliable Operation</h3>
                <p className="text-sm sm:text-base">
                  Precision-engineered mechanisms ensure consistent performance under varying conditions, 
                  with robust construction for long service life in demanding applications.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Complete Range</h3>
                <p className="text-sm sm:text-base">
                  From liquid drain to thermodynamic designs, we offer the right steam trap type 
                  for every application, pressure range, and operating condition.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-teal-600">Expert Support</h3>
                <p className="text-sm sm:text-base">
                  Comprehensive technical assistance for trap selection, sizing, installation, 
                  and maintenance to ensure optimal steam system performance.
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default SteamTraps;